import { process, State } from '@progress/kendo-data-query';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  GridDataStateChangeEvent,
  GridPagerSettings,
  GridRowProps,
} from '@progress/kendo-react-grid';
import { cloneElement, FC, useCallback, useEffect, useMemo, useState } from 'react';
import matchDocumentColumns from 'src/assets/static/invoice_scan_manager/matchDocumentColumns';
import Button from 'src/components/design-system/Button';
import GridWrapper from 'src/components/GridWrapper';
import Modal from 'src/components/Modal';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import InvoiceLineItem from 'src/sections/enter_invoice/InvoiceLineItem';
import useGetMatchInvoicesApi from 'src/services/useGetMatchInvoicesApi';
import { IMatchingInvoices } from 'src/types/IMatchingInvoice';
import DuplicateDocToolBar from './MatchDocumentTableToolBar';

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const rowRender = (trElement: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
  let trProps: any = {};
  trProps = {
    ...trElement.props,
    className: `${trElement.props.className} ${props.className} ${
      props.dataItem.preview ? 'border-preview-box' : ''
    }`,
  };
  return cloneElement(trElement, { ...trProps });
};

const defaultProps = {
  tableTextId: 'Records',
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  GridPagerSettings: gridPagerSettings,
  rowRender,
} as const;

export const initialDataState: State = {
  skip: 0,
  take: 20,
  sort: [
    {
      field: 'statusDate',
      dir: 'desc',
    },
  ],
};

interface MatchDocumentTableProps {
  isModalOpen: boolean;
  closeModal: () => void;
  docNumber: string;
}
const MatchDocumentTable: FC<MatchDocumentTableProps> = ({
  isModalOpen,
  closeModal,
  docNumber,
}) => {
  const {
    value: isLineItemModalOpen,
    setTrue: openLineItemModal,
    setFalse: closeLineItemModal,
  } = useBoolean(false);

  const { company: selectedCompany } = useCompany();
  const { getInvoiceMatches, invoiceMatchesLoading } = useGetMatchInvoicesApi();
  const [lineItem, setLineItem] = useState<IMatchingInvoices>();
  const [matchDocumentList, setMatchDocumentList] = useState<IMatchingInvoices[]>([]);
  const [dataState, setDataState] = useState(initialDataState);

  const { data, total } = useMemo(
    () => process(matchDocumentList, dataState),
    [dataState, matchDocumentList]
  );

  const InvoiceMatches = useCallback(async () => {
    if (docNumber && selectedCompany.companyId) {
      const response = await getInvoiceMatches({
        invoiceNumber: docNumber,
        companyId: selectedCompany.companyId,
      });
      setMatchDocumentList(response);
    }
  }, [docNumber, getInvoiceMatches, selectedCompany.companyId]);

  useEffect(() => {
    if (isModalOpen) {
      setMatchDocumentList([]);
      setDataState(initialDataState);
      InvoiceMatches();
    }
  }, [InvoiceMatches, isModalOpen]);

  const onDataStateChange = useCallback(
    (e: GridDataStateChangeEvent) => {
      setDataState(e.dataState);
    },
    [setDataState]
  );

  const cellClick = useCallback(
    (data: any) => {
      setLineItem(data);
      openLineItemModal();
    },
    [openLineItemModal, setLineItem]
  );

  return (
    <Modal
      title={`Showing matching documents for doc # ${docNumber}`}
      visible={isModalOpen}
      closeModal={closeModal}
      width="80%"
    >
      <GridWrapper
        id="matchDocument"
        columns={matchDocumentColumns}
        total={total}
        dataSource={data}
        dataState={dataState}
        onDataStateChange={onDataStateChange}
        pageable={gridPagerSettings}
        gridToolbarChildren={
          <DuplicateDocToolBar dataState={dataState} setDataState={setDataState} />
        }
        height="68vh"
        fieldModifiable
        cellClick={() => ({ cellClick })}
        {...defaultProps}
        loading={invoiceMatchesLoading}
      />

      <DialogActionsBar>
        <div className="flex justify-end mt-3">
          <Button color="info" onClick={closeModal}>
            Close
          </Button>
        </div>
      </DialogActionsBar>
      <Modal width="80%" visible={isLineItemModalOpen} closeModal={closeLineItemModal}>
        <InvoiceLineItem
          invoiceId={lineItem?.receiptId?.toString() || ''}
          showModal={closeLineItemModal}
          title={`Showing items for Invoice ${lineItem?.invoiceNumber}`}
          isOcr={true}
        />
      </Modal>
    </Modal>
  );
};

export default MatchDocumentTable;
